import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import {
  Settings,
  LogOut,
  ChevronLeft,
  Cpu,
  FlaskConical,
  ShieldCheck,
  BookOpen,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { LanguageSwitcher } from '../common/LanguageSwitcher';

// Admin emails
const ADMIN_EMAILS = ["c.merinoherrero@gmail.com", "emprendeconciente1111@gmail.com", "admin@invictus.trading"];

export const Sidebar = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const { logout, user } = useAuth();
  const { t } = useTranslation();
  
  // Check if user is admin
  const isAdmin = user && ADMIN_EMAILS.includes(user.email);

  // Navigation - Herramienta interna de Ops (bots). El modulo de inversores
  // (Mis Inversiones, Paquetes, Referidos, Bots HFT legacy) se retiro del menu.
  const navItems = [
    ...(isAdmin ? [
      { path: '/admin/ops', label: 'Ops Center', icon: Cpu, highlight: true },
      { path: '/admin/ops/backtest', label: 'Backtest Lab', icon: FlaskConical },
      { path: '/admin/ops/governor', label: 'Governor', icon: ShieldCheck },
      { path: '/admin/ops/strategies', label: 'Estrategias', icon: BookOpen, highlight: true },
    ] : []),
    { path: '/settings', label: 'Ajustes', icon: Settings },
  ];

  return (
    <aside
      data-testid="sidebar"
      className={cn(
        'fixed left-0 top-0 z-40 h-screen bg-card/80 backdrop-blur-xl border-r border-border transition-all duration-300',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className={cn(
          'flex items-center h-16 px-4 border-b border-border',
          collapsed ? 'justify-center' : 'justify-between'
        )}>
          {!collapsed && (
            <div className="flex items-center gap-2">
              <img src="/invictus-logo.png" alt="Invictus" className="w-10 h-10 object-contain" />
              <span className="font-heading font-bold text-lg bg-gradient-to-r from-amber-400 to-yellow-500 bg-clip-text text-transparent">Invictus</span>
            </div>
          )}
          {collapsed && (
            <img src="/invictus-logo.png" alt="Invictus" className="w-8 h-8 object-contain" />
          )}
          <Button
            variant="ghost"
            size="sm"
            className={cn('p-1', collapsed && 'absolute -right-3 top-6 bg-card border border-border rounded-full')}
            onClick={() => setCollapsed(!collapsed)}
            data-testid="sidebar-toggle"
          >
            <ChevronLeft className={cn('w-4 h-4 transition-transform', collapsed && 'rotate-180')} />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto scrollbar-thin">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.slice(1)}`}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-sm transition-all duration-200 group',
                  isActive
                    ? 'bg-primary/10 text-primary border border-primary/20'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent',
                  item.highlight && !isActive && 'bg-primary/5 border border-primary/10',
                  item.primary && !isActive && 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 text-green-400 hover:from-green-500/30 hover:to-emerald-500/30'
                )}
              >
                <Icon className={cn(
                  'w-5 h-5 flex-shrink-0', 
                  isActive && 'text-primary',
                  item.highlight && 'text-primary'
                )} />
                {!collapsed && (
                  <span className="font-medium text-sm">{item.label}</span>
                )}
                {item.badge && !collapsed && (
                  <span className={cn(
                    "ml-auto text-[10px] px-1.5 py-0.5 rounded font-medium",
                    item.badge === 'LIVE' 
                      ? "bg-green-500/20 text-green-400"
                      : item.badge === 'ADMIN'
                      ? "bg-red-500/20 text-red-400"
                      : "bg-primary/20 text-primary"
                  )}>
                    {item.badge}
                  </span>
                )}
                {isActive && !collapsed && !item.highlight && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* User section */}
        <div className={cn(
          'border-t border-border p-4',
          collapsed ? 'flex flex-col items-center gap-2' : ''
        )}>
          {!collapsed && (
            <>
              <div className="mb-3">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
              <div className="mb-3">
                <LanguageSwitcher variant="ghost" />
              </div>
            </>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            className={cn(
              'text-muted-foreground hover:text-destructive w-full justify-start',
              collapsed && 'justify-center p-2'
            )}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            {!collapsed && <span className="ml-2">{t('common.logout')}</span>}
          </Button>
        </div>
      </div>
    </aside>
  );
};
